import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { View, Button, Heading } from "@aws-amplify/ui-react";
import "./subscriptionSettings.scss";
import "./equipmentHealthSubscriptionSettings.scss";
import { useTranslation } from "react-i18next";
import { Auth } from "aws-amplify";
import {
  getEquipmentHealthSubscriptions,
  updateEquipmentHealthSubscriptions,
} from "../apis/equipmentHealthSubscriptionApi";
import NavBar from "../components/navbar/navbar";
import BasicToast from "../components/toastMessage/toast";

// Map of companyId -> array of subscribed testIds
const toSelection = (companies) =>
  Object.fromEntries(
    companies.map((c) => [
      c.CompanyId,
      c.Tests.filter((test) => test.IsSubscribed).map((test) => test.TestId),
    ])
  );

const groupBySubsystem = (tests) => {
  const groups = new Map();
  tests.forEach((test) => {
    if (!groups.has(test.Subsystem)) groups.set(test.Subsystem, []);
    groups.get(test.Subsystem).push(test);
  });
  return [...groups].map(([subsystem, items]) => ({ subsystem, tests: items }));
};

const groupBySeverity = (tests) => {
  const groups = new Map();
  tests.forEach((test) => {
    if (!groups.has(test.Severity)) groups.set(test.Severity, []);
    groups.get(test.Severity).push(test);
  });
  return [...groups].map(([severity, items]) => ({ severity, tests: items }));
};

const SeverityChip = ({ severity }) => (
  <span className={`severity-chip severity-${severity?.toLowerCase()}`}>
    {severity}
  </span>
);

const CompanyCheckbox = ({ checked, indeterminate, onChange }) => (
  <input
    type='checkbox'
    checked={checked}
    ref={(el) => {
      if (el) el.indeterminate = indeterminate;
    }}
    onChange={onChange}
  />
);

const EquipmentHealthSubscriptionSettings = ({ user, signOut }) => {
  const { t } = useTranslation("common");
  const [userInfo, setUserInfo] = useState();
  const [companies, setCompanies] = useState();
  const [selection, setSelection] = useState({});
  const [savedSelection, setSavedSelection] = useState({});
  const [isToastShown, setIsToastShown] = useState(false);
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();

  const toggleExpanded = (companyId) =>
    setExpanded((prev) => ({ ...prev, [companyId]: !prev[companyId] }));

  const fetchSubscriptions = async () => {
    const data = await getEquipmentHealthSubscriptions(userInfo.id);
    const initial = toSelection(data);

    setCompanies(data);
    setSelection(initial);
    setSavedSelection(initial);
  };

  const fetchUserInfo = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();

      setUserInfo({
        id: currentUser.attributes["custom:hyt_id"],
        companyIdsString: currentUser.attributes["custom:company_ids"],
        email: currentUser.attributes["email"],
      });
    } catch (error) {
      throw new Error("Error fetching user:", error);
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, []);

  useEffect(() => {
    if (!userInfo) return;
    fetchSubscriptions();
  }, [userInfo]);

  const handleCompanyChange = (company, isChecked) => {
    setSelection({
      ...selection,
      [company.CompanyId]: isChecked ? company.Tests.map((x) => x.TestId) : [],
    });
  };

  const handleSubsystemChange = (companyId, subsystemTests, isChecked) => {
    const ids = subsystemTests.map((x) => x.TestId);
    const current = (selection[companyId] ?? []).filter((id) => !ids.includes(id));
    setSelection({
      ...selection,
      [companyId]: isChecked ? [...current, ...ids] : current,
    });
  };

  const handleSeverityChange = (companyId, severityTests, isChecked) => {
    const ids = severityTests.map((x) => x.TestId);
    const current = (selection[companyId] ?? []).filter((id) => !ids.includes(id));
    setSelection({
      ...selection,
      [companyId]: isChecked ? [...current, ...ids] : current,
    });
  };

  const isError = (test) => test.Severity?.toLowerCase() === "error";

  const errorTotal = (companies ?? []).reduce(
    (n, c) => n + c.Tests.filter(isError).length,
    0
  );
  const errorSelected = (companies ?? []).reduce(
    (n, c) =>
      n + c.Tests.filter((x) => isError(x) && (selection[c.CompanyId] ?? []).includes(x.TestId)).length,
    0
  );

  const handleAllErrorsChange = (isChecked) => {
    const next = { ...selection };
    companies.forEach((c) => {
      const errorIds = c.Tests.filter(isError).map((x) => x.TestId);
      const others = (selection[c.CompanyId] ?? []).filter((id) => !errorIds.includes(id));
      next[c.CompanyId] = isChecked ? [...others, ...errorIds] : others;
    });
    setSelection(next);
  };

  const handleTestChange = (companyId, testId, isChecked) => {
    const current = selection[companyId] ?? [];
    setSelection({
      ...selection,
      [companyId]: isChecked
        ? [...current, testId]
        : current.filter((id) => id !== testId),
    });
  };

  const handleSave = async () => {
    const hasChanges = companies.some((c) => {
      const now = [...(selection[c.CompanyId] ?? [])].sort();
      const before = [...(savedSelection[c.CompanyId] ?? [])].sort();
      return JSON.stringify(now) !== JSON.stringify(before);
    });

    if (!hasChanges) return;

    await updateEquipmentHealthSubscriptions(userInfo.id, companies, selection);
    await fetchSubscriptions();
    setIsToastShown(true);
  };

  const handleReset = () => {
    setSelection(savedSelection);
  };

  const handleGoBack = () => {
    navigate("/home");
  };

  return (
    <View className='page-container'>
      <NavBar user={user} signOut={signOut} />
      <Button className='back-button' onClick={handleGoBack}>
        &lt; {t("SUBSCRIPTIONMANAGEMENT.backToHome")}
      </Button>
      <Heading
        className='text-color-subscription-page'
        level={3}
        textAlign={"center"}
      >
        {t("SUBSCRIPTIONMANAGEMENT.equipmentHealthSubscriptionSettings")}
      </Heading>

      <View marginTop='large' className='email-section'>
        <label className='text-color-subscription-page '>
          {t("SUBSCRIPTIONMANAGEMENT.emailAddress")} :
        </label>
        <input
          type='text'
          value={userInfo?.email}
          style={{ width: `${userInfo?.email?.length}ch` }}
          disabled
        />
      </View>

      {companies && companies.length > 0 ? (
        <>
          {errorTotal > 0 && (
            <View className='severity-select'>
              <label className='severity-select-row'>
                <CompanyCheckbox
                  checked={errorSelected === errorTotal}
                  indeterminate={errorSelected > 0 && errorSelected < errorTotal}
                  onChange={(e) => handleAllErrorsChange(e.target.checked)}
                />
                <SeverityChip severity='Error' />
                ({errorTotal})
              </label>
            </View>
          )}
          <View className='company-list'>
            {companies.map((company) => {
              const selected = selection[company.CompanyId] ?? [];
              const allChecked =
                company.Tests.length > 0 && selected.length === company.Tests.length;

              return (
                <div className='company-item' key={company.CompanyId}>
                  <div className='company-header'>
                    <label className='company-row'>
                      <CompanyCheckbox
                        checked={allChecked}
                        indeterminate={selected.length > 0 && !allChecked}
                        onChange={(e) => handleCompanyChange(company, e.target.checked)}
                      />
                      {company.CompanyName}
                    </label>
                    <button
                      type='button'
                      className='company-toggle'
                      aria-expanded={!!expanded[company.CompanyId]}
                      onClick={() => toggleExpanded(company.CompanyId)}
                    >
                      {selected.length}/{company.Tests.length}
                      <span className='chevron'>
                        {expanded[company.CompanyId] ? "\u25B2" : "\u25BC"}
                      </span>
                    </button>
                  </div>
                  {selected.length > 0 && !allChecked && (
                    <div className='selected-tests'>
                      {groupBySeverity(company.Tests).flatMap(({ severity, tests }) => {
                        const chosen = tests.filter((test) => selected.includes(test.TestId));
                        if (chosen.length === 0) return [];
                        if (chosen.length === tests.length) {
                          return [
                            <span
                              className={`selected-chip severity-${severity?.toLowerCase()}`}
                              key={`all-${severity}`}
                              title={severity}
                            >
                              All {severity} tests
                            </span>,
                          ];
                        }
                        return chosen.map((test) => (
                          <span
                            className={`selected-chip severity-${severity?.toLowerCase()}`}
                            key={test.TestId}
                            title={severity}
                          >
                            {test.TestId} - {test.TestName}
                          </span>
                        ));
                      })}
                    </div>
                  )}
                  {expanded[company.CompanyId] && (
                  <div className='test-list'>
                    <div className='severity-select'>
                      {groupBySeverity(company.Tests).map(({ severity, tests }) => {
                        const count = tests.filter((x) => selected.includes(x.TestId)).length;
                        return (
                          <label className='severity-select-row' key={severity}>
                            <CompanyCheckbox
                              checked={count === tests.length}
                              indeterminate={count > 0 && count < tests.length}
                              onChange={(e) =>
                                handleSeverityChange(company.CompanyId, tests, e.target.checked)
                              }
                            />
                            <SeverityChip severity={severity} />
                            ({tests.length})
                          </label>
                        );
                      })}
                    </div>
                    {groupBySubsystem(company.Tests).map(({ subsystem, tests }) => {
                      const count = tests.filter((x) => selected.includes(x.TestId)).length;
                      return (
                        <div className='subsystem-group' key={subsystem}>
                          <label className='subsystem-row'>
                            <CompanyCheckbox
                              checked={count === tests.length}
                              indeterminate={count > 0 && count < tests.length}
                              onChange={(e) =>
                                handleSubsystemChange(company.CompanyId, tests, e.target.checked)
                              }
                            />
                            {subsystem}
                          </label>
                          {tests.map((test) => (
                            <label className='test-row' key={test.TestId}>
                              <input
                                type='checkbox'
                                checked={selected.includes(test.TestId)}
                                onChange={(e) =>
                                  handleTestChange(company.CompanyId, test.TestId, e.target.checked)
                                }
                              />
                              {test.TestId} - {test.TestName}
                              <SeverityChip severity={test.Severity} />
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              );
            })}
          </View>
          <View className='bottom-button-container'>
            <Button
              onClick={handleSave}
              variation='default'
              className='save-button'
            >
              {t("SUBSCRIPTIONMANAGEMENT.save")}
            </Button>
            <Button
              onClick={handleReset}
              variation='link'
              className='reset-button'
            >
              {t("SUBSCRIPTIONMANAGEMENT.reset")}
            </Button>
          </View>
        </>
      ) : (
        <div>{t("SUBSCRIPTIONMANAGEMENT.noActiveSubscription")} </div>
      )}
      <BasicToast
        isShown={isToastShown}
        setIsShown={setIsToastShown}
        bodyMessage={t("SUBSCRIPTIONMANAGEMENT.saveSuccess")}
      />
    </View>
  );
};

export default EquipmentHealthSubscriptionSettings;
